/**
 * TrustGrid Context
 * Manages currently selected TrustGrid project across components
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useUser } from './UserContext';

const TrustGridContext = createContext();

export const useTrustGrid = () => {
  const context = useContext(TrustGridContext);
  if (!context) {
    throw new Error('useTrustGrid must be used within TrustGridProvider');
  }
  return context;
};

export const TrustGridProvider = ({ children }) => {
  const { user } = useUser();

  // Store selected project ID in localStorage for persistence
  // SECURITY: Validate user ID on initial load to prevent cross-org data access
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    const stored = localStorage.getItem('trustgrid_selected_project');
    const storedUserId = localStorage.getItem('trustgrid_user_id');
    const currentUserId = user?.id ? String(user.id) : null;

    // If stored user ID doesn't match current user, clear the stored project
    if (storedUserId && currentUserId && storedUserId !== currentUserId) {
      console.log('[TrustGrid] Initial load: User mismatch, clearing project selection');
      localStorage.removeItem('trustgrid_selected_project');
      return null;
    }

    // Otherwise, load the stored project
    return stored === 'all' ? 'all' : (stored ? parseInt(stored, 10) : null);
  });

  const [selectedProject, setSelectedProject] = useState(null);

  // SECURITY: Clear project selection when user changes (logout/login)
  // This ensures new user doesn't see previous user's selected project
  useEffect(() => {
    const storedUserId = localStorage.getItem('trustgrid_user_id');
    const currentUserId = user?.id ? String(user.id) : null;

    // If user changed or logged out, clear project selection
    if (storedUserId && storedUserId !== currentUserId) {
      console.log('[TrustGrid] User session changed, clearing project selection');
      setSelectedProjectId(null);
      setSelectedProject(null);
      localStorage.removeItem('trustgrid_selected_project');
    }

    // Store current user ID for future validation
    if (currentUserId) {
      localStorage.setItem('trustgrid_user_id', currentUserId);
    } else {
      // User logged out, clear stored user ID
      localStorage.removeItem('trustgrid_user_id');
    }
  }, [user]); // Re-run when user object changes

  // SECURITY: Validate selected project belongs to current user's org
  // If project doesn't exist (cross-org access), clear selection
  useEffect(() => {
    const validateProject = async () => {
      // Only validate if we have both user and a numeric project ID
      if (!user || !selectedProjectId || selectedProjectId === 'all') {
        return;
      }

      try {
        const { getProject } = await import('../modules/trustgrid/api/trustgridApi');
        await getProject(selectedProjectId);
        // Project exists and user has access - keep selection
      } catch (error) {
        // Project doesn't exist or user doesn't have access (404 = cross-org)
        if (error.response?.status === 404) {
          console.warn('[TrustGrid] Selected project not accessible (cross-org), clearing selection');
          setSelectedProjectId(null);
          setSelectedProject(null);
          localStorage.removeItem('trustgrid_selected_project');
        }
        // Other errors (network, etc.) - don't clear selection
      }
    };

    validateProject();
  }, [user, selectedProjectId]); // Validate when user or project changes

  // Persist selected project ID to localStorage
  useEffect(() => {
    if (selectedProjectId === 'all') {
      localStorage.setItem('trustgrid_selected_project', 'all');
    } else if (selectedProjectId) {
      localStorage.setItem('trustgrid_selected_project', selectedProjectId.toString());
    } else {
      localStorage.removeItem('trustgrid_selected_project');
    }
  }, [selectedProjectId]);

  const selectProject = (projectId, projectData = null) => {
    setSelectedProjectId(projectId);
    setSelectedProject(projectData);
  };

  const clearSelectedProject = () => {
    setSelectedProjectId(null);
    setSelectedProject(null);
  };

  const value = {
    selectedProjectId,
    selectedProject,
    selectProject,
    clearSelectedProject,
    isAllProjects: selectedProjectId === 'all'
  };

  return (
    <TrustGridContext.Provider value={value}>
      {children}
    </TrustGridContext.Provider>
  );
};

export default TrustGridContext;
